import { fromPromise, setup, assign } from 'xstate';
import {
  getUserProfile,
  type UserProfileAggregate,
} from '@leagueplatform/user-profile-api';
import { createActorContext } from '@xstate/react';
import { captureAppError } from '@leagueplatform/observability';
import { socketAsFetch } from 'common/utils/socket-as-fetch';
import {
  MESSAGE_TYPES,
  PHONE_NUMBER_TYPES,
  SENTRY_CONSTANTS,
  TERMS_AND_CONDITIONS_VERSION,
} from 'common/constants';
import {
  patchCommunicationPreferences,
  buildPreferenceDelta,
} from 'common/communication-preferences.api';
import {
  mapPlanSelectionResolution,
  type PlanSelectionResolution,
  type ResolvePlanSelectionResponse,
} from 'components/onboarding/plan-selection-resolution';
import {
  mapEligibilityDecision,
  type AppInitDetailsResponse,
  type EligibilityDecision,
} from 'components/onboarding/app-init-eligibility';
import { isPlanSwitcherEnabled } from 'components/onboarding/plan-switcher-capability';
import {
  PROMOTIONS_PREFERENCE_IDS,
  TOGGLE_OPTION_NAME,
} from 'common/communication-preferences.constants';
import {
  resolveFtuxComplete,
  simulateDevFtuxSetFailure,
} from './ftux-dev-override';

type OnboardingMachineContextType = {
  onboardingCompleted: boolean;
  email: string;
  mobilePhoneNumber: string;
  preferredPhoneNumber: string;
  preferredFirstName: string;
  profileMobilePhoneNumber: string;
  profilePreferredPhoneNumber: string;
  marketingEmailsOptIn: boolean;
  errorCause: Error | null;
};

type FetchUserProfileOutput = Omit<
  OnboardingMachineContextType,
  | 'email'
  | 'mobilePhoneNumber'
  | 'preferredPhoneNumber'
  | 'marketingEmailsOptIn'
  | 'errorCause'
>;

// Shape of the info payload accepted by the accept_member_terms v2 socket
// message. Lives at the call site so a typo in `version` fails type-check.
// `version` MUST be a string: the backend's Request.Version is a Go string
// and the decode layer drops (not coerces) a JSON number, silently
// persisting version "" in the member's T&C audit record.
type AcceptMemberTermsInfo = { version: string };

// UserProfileAggregate types user_id, email, the top-level ftux_complete
// flag (the account-level onboarding-complete marker, shared with iOS and
// Android) and the nested user_profile. The only field the SDK does not
// surface is phone_numbers — the list of contact phones populated by Account
// Setup — so we extend to read it.
type GeisingerUserProfileData = UserProfileAggregate & {
  phone_numbers?: Array<{ description?: string; number?: string }>;
};

const findPhoneNumber = (
  phoneNumbers: GeisingerUserProfileData['phone_numbers'],
  description: string,
) => phoneNumbers?.find((p) => p.description === description)?.number || '';

// The single flow event. `payload` carries the Account Setup form values
// into context and is optional — views that only advance (e.g. the T&C
// placeholder) send the event bare. Later onboarding steps widen the
// payload with their own optional fields.
type NavigateToNextViewEvent = {
  type: 'navigateToNextView';
  payload?: Partial<
    Pick<
      OnboardingMachineContextType,
      | 'preferredFirstName'
      | 'mobilePhoneNumber'
      | 'preferredPhoneNumber'
      | 'email'
      | 'marketingEmailsOptIn'
    >
  >;
};

// Sent by the server-driven error screen's "Try again" (GEIS-621), which emits
// a retry deeplink the client intercepts. Re-enters resolvingPlanSelection so
// bootstrap re-runs in place rather than reloading the page.
type RetryEvent = { type: 'retry' };

type OnboardingEvent = NavigateToNextViewEvent | RetryEvent;

// Resilience for the account-completion write (set_post_ftux): a transient
// failure that leaves ftux_complete unset would re-show account-level
// onboarding on every login, so retry a few times with a short linear backoff
// before giving up.
const SET_POST_FTUX_MAX_ATTEMPTS = 3;
const SET_POST_FTUX_RETRY_DELAY_MS = 250;

const delayMilliseconds = (milliseconds: number) =>
  new Promise((resolve) => {
    setTimeout(resolve, milliseconds);
  });

// Sends set_post_ftux, retrying transient failures with a short linear backoff.
// Once attempts are exhausted it rejects with the last error for the invoking
// actor to handle. Written recursively (not a for/while loop) so each attempt's
// await stays sequential without an in-loop await.
const sendSetPostFtuxWithRetry = async (attempt = 1): Promise<void> => {
  try {
    simulateDevFtuxSetFailure(attempt);
    await socketAsFetch({
      message_type: MESSAGE_TYPES.SET_POST_FTUX,
      info: { ftux_complete: true },
    });
  } catch (error) {
    if (attempt >= SET_POST_FTUX_MAX_ATTEMPTS) {
      throw error instanceof Error ? error : new Error(String(error));
    }
    await delayMilliseconds(SET_POST_FTUX_RETRY_DELAY_MS * attempt);
    await sendSetPostFtuxWithRetry(attempt + 1);
  }
};

// The socket layer has no per-request deadline, so a bootstrap call that never
// answers — service unreachable, or not yet deployed — would hold the member on
// a full-screen loader indefinitely. Cap the wait and degrade. 5s matches the
// Android client's budget.
const BOOTSTRAP_REQUEST_TIMEOUT_MS = 5000;

// Sentinel the deadline resolves with. A plain literal, so the caller narrows it
// off the union without a type assertion.
const REQUEST_TIMED_OUT = 'bootstrapRequestTimedOut';

// Resolves whatever the request resolves, or REQUEST_TIMED_OUT if the deadline
// wins. The timer is always cleared, so a request that answers first leaves
// nothing pending behind it.
const raceBootstrapDeadline = async <Response>(request: Promise<Response>) => {
  let timer: ReturnType<typeof setTimeout> | undefined;

  // When the deadline wins, the request is still in flight and nothing is awaiting it,
  // so a later rejection would surface as an unhandled rejection — and socketAsFetch
  // has already reported it to the SDK once. Covers both callers; `Promise.race` below
  // keeps its own handler on the same promise.
  request.catch(() => {});

  try {
    return await Promise.race([
      request,
      new Promise<typeof REQUEST_TIMED_OUT>((resolve) => {
        timer = setTimeout(
          () => resolve(REQUEST_TIMED_OUT),
          BOOTSTRAP_REQUEST_TIMEOUT_MS,
        );
      }),
    ]);
  } finally {
    clearTimeout(timer);
  }
};

export const onboardingMachine = setup({
  types: {
    context: {} as OnboardingMachineContextType,
    events: {} as OnboardingEvent,
  },
  actors: {
    // Fails open on every non-answer: an outage must not lock covered members out.
    checkEligibility: fromPromise<EligibilityDecision>(async () => {
      try {
        const request: Promise<AppInitDetailsResponse> = socketAsFetch({
          message_type: MESSAGE_TYPES.GET_APP_INIT_DETAILS,
          info: { platform: 'web' },
        });
        const response = await raceBootstrapDeadline(request);

        if (response === REQUEST_TIMED_OUT) {
          // The call is still in flight, so the SDK never sees a rejection to capture.
          captureAppError(
            new Error('get_app_init_details exceeded its client deadline'),
            {
              ownershipTeam: SENTRY_CONSTANTS.OWNERSHIP_TEAM,
              errorName: 'Get App Init Details Timed Out',
            },
          );
          return 'unknown';
        }

        return mapEligibilityDecision(response);
      } catch {
        // socketAsFetch already hands the SDK an errorContext; reporting again duplicates it.
        return 'unknown';
      }
    }),
    fetchUserProfileData: fromPromise<FetchUserProfileOutput>(async () => {
      const data = (await getUserProfile()) as GeisingerUserProfileData;

      return {
        // Returning-member gate: the platform FTUX-complete flag is the
        // canonical account-level onboarding marker, read from the same
        // top-level source as iOS and Android (parity). It is set server-side
        // only when the member finishes the whole flow at All Set (see the
        // setPostFtux actor below), so a member who accepts T&C but abandons
        // before All Set correctly re-walks onboarding on the next login.
        // `=== true` treats a null/absent flag on a brand-new profile as
        // not-complete, matching the other platforms' nil handling.
        // resolveFtuxComplete is an inert pass-through in production; in dev it
        // lets the real (always-complete) server value be overridden so the
        // first-time flow is reachable locally.
        onboardingCompleted: resolveFtuxComplete(data.ftux_complete) === true,
        profileMobilePhoneNumber: findPhoneNumber(
          data.phone_numbers,
          PHONE_NUMBER_TYPES.MOBILE,
        ),
        profilePreferredPhoneNumber: findPhoneNumber(
          data.phone_numbers,
          PHONE_NUMBER_TYPES.PREFERRED,
        ),
        preferredFirstName: data.user_profile?.preferred_first_name ?? '',
      };
    }),
    acceptTerms: fromPromise(
      async ({
        input,
      }: {
        input: Pick<OnboardingMachineContextType, 'marketingEmailsOptIn'>;
      }) => {
        // 1. Persist T&C acceptance. Server populates
        //    userProfile.memberTermsAndConditions.dateAccepted — the record of
        //    consent only. The returning-member gate no longer reads this; it
        //    reads the top-level ftux_complete flag set at All Set (see
        //    fetchUserProfileData above). socketAsFetch already wraps any
        //    failure with an `errorName` of
        //    "Geisinger Socket Fetch Response Error - <message_type>", so
        //    the original error propagates with full cause chain intact.
        const acceptInfo: AcceptMemberTermsInfo = {
          version: TERMS_AND_CONDITIONS_VERSION,
        };
        await socketAsFetch({
          message_type: MESSAGE_TYPES.ACCEPT_MEMBER_TERMS,
          info: acceptInfo,
        });

        // 2. Marketing-emails opt-in (optional) — only sent when the member
        //    explicitly checked the box. Per Confluence §4.3, PATCH leaves
        //    omitted sections untouched, so an unchecked box is a no-op.
        //    Best-effort: T&C acceptance above has already persisted, so a
        //    failed optional opt-in must not strand the member on the error
        //    page — the returning-member gate would then skip onboarding on
        //    the next load and the opt-in would be lost with no retry path.
        //    Report to Sentry and continue; the Communication Preferences
        //    page in Settings remains the self-service recovery path.
        if (input.marketingEmailsOptIn) {
          // GEIS-500: the marketing opt-in now persists through the SAME real
          // Comm Prefs resolver path as paperless (`patchCommunicationPreferences`),
          // replacing the GEIS-334 placeholder `{ promotionsAndIncentives }` body.
          // Opting into marketing emails turns the promotions master + email
          // channel on and leaves SMS/phone off; the delta carries each as a
          // stringified boolean, exactly as the Settings screen sends them.
          const delta = buildPreferenceDelta([
            {
              id: PROMOTIONS_PREFERENCE_IDS.receiveUpdates,
              optionName: TOGGLE_OPTION_NAME,
              value: 'true',
            },
            {
              id: PROMOTIONS_PREFERENCE_IDS.email,
              optionName: TOGGLE_OPTION_NAME,
              value: 'true',
            },
            {
              id: PROMOTIONS_PREFERENCE_IDS.sms,
              optionName: TOGGLE_OPTION_NAME,
              value: 'false',
            },
            {
              id: PROMOTIONS_PREFERENCE_IDS.phone,
              optionName: TOGGLE_OPTION_NAME,
              value: 'false',
            },
          ]);
          try {
            await patchCommunicationPreferences(delta);
          } catch (error) {
            captureAppError(
              error instanceof Error ? error : new Error(String(error)),
              {
                ownershipTeam: SENTRY_CONSTANTS.OWNERSHIP_TEAM,
                errorName: 'Error saving marketing communications opt-in',
              },
            );
          }
        }
      },
    ),
    // Resolves the member's plan selection at session bootstrap (GEIS plan-switcher
    // bootstrap): reads the member's plans, persists the resolved selection
    // server-side, and returns a signal the machine routes on. Re-derived every
    // login — a persisted id is never trusted as a skip signal.
    //
    // A transport failure (service unreachable or not yet deployed), or a request
    // that outlives its client deadline, maps to `unavailable` so the flow falls
    // back to the existing gate rather than stranding the member — the bootstrap
    // call is additive and needs no version gate. A successful `error` status is
    // the server's fail-closed state and is distinct (retryable error screen).
    resolvePlanSelection: fromPromise<PlanSelectionResolution>(async () => {
      // Capability gate: a client without the plan switcher never calls the
      // service — it goes straight to Home. The server also returns
      // `not_applicable` as a safety net for a mis-gated client.
      if (!isPlanSwitcherEnabled()) {
        return 'notApplicable';
      }
      try {
        // The socket layer does not type responses per message (it resolves
        // `any`), so annotate the request as the resolve_plan_selection response
        // shape at the call site.
        const request: Promise<ResolvePlanSelectionResponse> = socketAsFetch({
          message_type: MESSAGE_TYPES.RESOLVE_PLAN_SELECTION,
        });
        const response = await raceBootstrapDeadline(request);

        if (response === REQUEST_TIMED_OUT) {
          // Nothing else reports a timeout — the socket call is still in flight,
          // so the SDK never sees a rejection to capture. Report it here, then
          // degrade to the gate just like a transport failure.
          captureAppError(
            new Error('resolve_plan_selection exceeded its client deadline'),
            {
              ownershipTeam: SENTRY_CONSTANTS.OWNERSHIP_TEAM,
              errorName: 'Resolve Plan Selection Timed Out',
            },
          );
          return 'unavailable';
        }

        const resolution = mapPlanSelectionResolution(response);

        // A fail-closed `error` arrives on a *successful* response, so the
        // socket layer never reports it and the member would land on the
        // retryable error screen with no Sentry trail. Report it here. Covers
        // both the server's explicit `error` status and a `resolved` carrying no
        // usable plan id. `status` is a contract enum, never member data.
        if (resolution === 'error') {
          captureAppError(
            new Error(
              `Plan selection failed closed: status=${response?.status}`,
            ),
            {
              ownershipTeam: SENTRY_CONSTANTS.OWNERSHIP_TEAM,
              errorName: 'Resolve Plan Selection Failed Closed',
            },
          );
        }

        return resolution;
      } catch {
        // Transport failure. Deliberately not reported here: socketAsFetch
        // already hands the SDK an errorContext ("Geisinger Socket Fetch
        // Response Error - <message_type>") and the SDK captures the rejection,
        // so a second report would only duplicate it. Degrade to the existing
        // gate.
        return 'unavailable';
      }
    }),
    // Marks account-level onboarding complete server-side. set_post_ftux flips
    // the platform ftux_complete flag, which the returning-member gate reads on
    // the next login so the account-level flow shows exactly once per account
    // (parity with iOS/Android). Fired only here — at the very end of the flow,
    // never at T&C — so an abandoned flow does not mark the account complete.
    //
    // The write is awaited and retried (see sendSetPostFtuxWithRetry): a single
    // transient socket failure that left ftux_complete unset would re-show
    // onboarding on every subsequent login. If every attempt fails the actor
    // fails open — it reports the error to Sentry and resolves normally, so the
    // flow still finishes instead of ejecting a member who has already left All
    // Set (the same best-effort pattern as the paperless/marketing writes).
    setPostFtux: fromPromise(async () => {
      try {
        await sendSetPostFtuxWithRetry();
      } catch (error) {
        // Fail-open: every retry has failed, but T&C and preferences are
        // already persisted and the member has already left All Set (the
        // "Start" CTA navigates as it sends), so surfacing a full-page error
        // here would eject them mid-flow over a non-critical write. Report to
        // Sentry and resolve; the only consequence is account-level onboarding
        // re-showing on the next login.
        captureAppError(
          error instanceof Error ? error : new Error(String(error)),
          {
            ownershipTeam: SENTRY_CONSTANTS.OWNERSHIP_TEAM,
            errorName: 'Error marking FTUX complete',
          },
        );
      }
    }),
  },
}).createMachine({
  id: 'onboarding',
  initial: 'checkingEligibility',
  context: {
    onboardingCompleted: false,
    email: '',
    mobilePhoneNumber: '',
    preferredPhoneNumber: '',
    preferredFirstName: '',
    profileMobilePhoneNumber: '',
    profilePreferredPhoneNumber: '',
    marketingEmailsOptIn: false,
    errorCause: null,
  },
  states: {
    checkingEligibility: {
      invoke: {
        src: 'checkEligibility',
        onDone: [
          {
            // Inline (not a setup() guard) so `event` keeps the narrowed
            // `xstate.done.actor.*` type with `output`.
            guard: ({ event }) => event.output === 'ineligible',
            target: 'ineligible',
          },
          { target: 'initializing' },
        ],
        // The actor maps every failure to 'unknown', so this is unreachable in practice.
        onError: { target: 'initializing' },
      },
    },
    // Terminal: Close signs out through a full-page redirect.
    ineligible: { type: 'final' },
    initializing: {
      invoke: {
        src: 'fetchUserProfileData',
        onDone: [
          {
            // Inline (not a setup() guard) so `event` keeps the narrowed
            // `xstate.done.actor.*` type with `output` — named guards are
            // typed against the machine's events union, which the internal
            // done event is not part of.
            guard: ({ event }) => !!event.output.onboardingCompleted,
            // Returning members skip onboarding but resolve their plan selection
            // at bootstrap before Home. The resolve step decides whether the Plan
            // Switcher gate shows at all — a single-plan member is resolved
            // server-side and skips straight to Home (no gate mounts, no flash).
            // First-login members hit the in-flow `planSwitcher` step instead.
            target: 'resolvingPlanSelection',
            actions: assign(({ event }) => event.output),
          },
          {
            target: 'featureHighlights',
            actions: assign(({ event }) => event.output),
          },
        ],
        onError: {
          target: 'error',
          actions: assign({
            errorCause: ({ event }) =>
              event.error instanceof Error
                ? event.error
                : new Error(String(event.error)),
          }),
        },
      },
    },
    // Feature Highlights reel (ON-01). Post-login on web: the SDK fetches its
    // slides through the authenticated leagueFetch, unlike iOS/Android.
    featureHighlights: {
      on: {
        navigateToNextView: { target: 'accountSetup' },
      },
    },
    accountSetup: {
      on: {
        navigateToNextView: {
          target: 'termsAndConditions',
          actions: assign(({ event }) => ({
            ...event.payload,
          })),
        },
      },
    },
    termsAndConditions: {
      on: {
        navigateToNextView: {
          target: 'acceptingTerms',
          actions: assign(({ context, event }) => ({
            marketingEmailsOptIn:
              context.email.trim() !== '' &&
              (event.payload?.marketingEmailsOptIn ?? false),
          })),
        },
      },
    },
    acceptingTerms: {
      invoke: {
        src: 'acceptTerms',
        input: ({ context }) => ({
          marketingEmailsOptIn: context.marketingEmailsOptIn,
        }),
        onDone: { target: 'planSwitcher' },
        onError: {
          target: 'error',
          actions: assign({
            errorCause: ({ event }) =>
              event.error instanceof Error
                ? event.error
                : new Error(String(event.error)),
          }),
        },
      },
    },
    // First-login Plan Switcher gate (GEIS-498), which now follows Terms directly:
    // the standalone Paperless step it sat behind was folded into the Account
    // Setup checkbox (GEIS-579), whose opt-in rides along with that screen's own
    // Comm Prefs PATCH. Sits
    // before the All Set completion screen. The PlanSwitcherStepView surfaces
    // the server-driven planSwitcher$root node; on its "/member/home"
    // completion deeplink the view sends navigateToNextView to advance to All
    // Set rather than routing Home. The returning-member gate (post-onboarding,
    // pre-Home) lives outside this machine in OnboardingGate, so a first-login
    // member sees the gate exactly once.
    planSwitcher: {
      on: {
        navigateToNextView: { target: 'allSet' },
      },
    },
    // Plan-selection bootstrap for returning members: call resolve_plan_selection
    // and route on the signal, before Home renders.
    resolvingPlanSelection: {
      invoke: {
        src: 'resolvePlanSelection',
        onDone: [
          {
            // Single plan resolved server-side → skip the gate, go straight to
            // Home (removes the single-plan flash). `notApplicable` (capability
            // off, or server not_applicable when the tenant has no plans
            // extension) likewise goes Home with no gate.
            guard: ({ event }) =>
              event.output === 'resolved' || event.output === 'notApplicable',
            target: 'onboardingComplete',
          },
          {
            guard: ({ event }) => event.output === 'noPlans',
            target: 'noCoverage',
          },
          {
            guard: ({ event }) => event.output === 'error',
            target: 'planSelectionError',
          },
          {
            // 'needsSelection' (multi-plan) and 'unavailable' (service
            // unreachable / not deployed) both show the existing gate — the
            // latter preserves today's behaviour as an additive fallback.
            target: 'returningPlanSwitcher',
          },
        ],
        // Defensive: the actor already maps transport failures to 'unavailable',
        // so an onError here is unexpected — fall back to the gate rather than
        // stranding the member.
        onError: { target: 'returningPlanSwitcher' },
      },
    },
    // No coverage (resolve_plan_selection → no_plans) and the failed-closed
    // plan-selection error both render the same server-driven screen
    // (planSwitcher$errorRoot, GEIS-621), whose "Try again" sends `retry`.
    //
    // Neither is `final` any more. They were, because the old views offered
    // either nothing to retry or a full page reload; the shared screen retries
    // in place, and a final state cannot be transitioned out of. Retry re-enters
    // resolvingPlanSelection, so a member whose coverage has since appeared —
    // or whose failure was transient — gets through without reloading the app.
    // Neither state has any other transition, so the member still cannot reach
    // the portal past a failed bootstrap.
    noCoverage: {
      on: {
        retry: { target: 'resolvingPlanSelection' },
      },
    },
    planSelectionError: {
      on: {
        retry: { target: 'resolvingPlanSelection' },
      },
    },
    // Returning-login Plan Switcher gate (GEIS-498): surfaced before Home for
    // multi-plan members. On the gate's "/member/home" completion the view
    // routes Home (the default deeplink behaviour) and sends navigateToNextView
    // to finish the machine, after which OnboardingGate renders the requested
    // route.
    returningPlanSwitcher: {
      on: {
        navigateToNextView: { target: 'onboardingComplete' },
      },
    },
    allSet: {
      on: {
        // Both CTAs share this event — "Start" additionally calls
        // useNavigate() in the view to deep-link to the health profile
        // questionnaire; "Not now" lets the container's fall-through render
        // the home page. Either way the flow first marks FTUX complete so the
        // account-level steps never re-show on a later login.
        navigateToNextView: { target: 'completingOnboarding' },
      },
    },
    // Persists account-level completion (set_post_ftux) for first-login
    // members, then finalizes. Returning members reach onboardingComplete via
    // returningPlanSwitcher and never pass through here, so the flag is written
    // exactly once, at true end-of-flow. The gate has no matching view for this
    // state, so OnboardingGate falls through to render the destination the All
    // Set CTA already routed to — no interstitial while the socket resolves.
    // The setPostFtux actor fails open (see above): a failed write is reported
    // to Sentry and still resolves, so this state always advances to
    // onboardingComplete and never ejects a member who has left All Set.
    completingOnboarding: {
      invoke: {
        src: 'setPostFtux',
        onDone: { target: 'onboardingComplete' },
      },
    },
    onboardingComplete: { type: 'final' },
    error: { type: 'final' },
  },
});

export const OnboardingMachineContext = createActorContext(onboardingMachine);
